<script setup lang="ts">
    import type { AiMessage } from '~/stores/aiAssistantTypes';
    import { renderMarkdown } from '~/utils/renderMarkdown';
    import { ENTITY_LABEL_KEY_MAP } from '~/utils/aiEntityMeta';

    defineProps<{
        message: AiMessage;
    }>();

    const { t } = useTranslation();

    const renderedContent = (content: string) => renderMarkdown(content);

    const documentsCreated = (message: AiMessage) => message.documentCreated ?? [];

    const getArtifactTitle = (doc: { type: string; id: string }) =>
        t(ENTITY_LABEL_KEY_MAP[doc.type] ?? 'Document');

    const formattedTime = (d: Date) =>
        `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;

    const autoFixResolved = (message: AiMessage) =>
        message.autoFix?.applied === true || message.autoFix?.dismissed === true;

    const autoFixLabel = (message: AiMessage) => {
        if (message.autoFix?.applied) return t('ai.chat.verification.auto_fix_applied');
        if (message.autoFix?.dismissed) return t('ai.chat.verification.auto_fix_dismissed');

        return t('ai.chat.verification.auto_fix_attention');
    };
</script>

<template>
    <div class="flex flex-col gap-[5px] items-start px-[15px] py-[8px] w-full">
        <div class="max-w-[680px] w-full">
            <!-- Auto-fix chip — this reply was triggered by the verification flow, not by a user message. -->
            <div
                v-if="message.autoFix"
                class="inline-flex items-center gap-2 mb-[6px] px-[14px] py-[7px] rounded-md font-inter text-[11px] leading-[14px]"
                :class="autoFixResolved(message)
                    ? 'text-[#1a8754] bg-[#1a8754]/[0.08] border border-dashed border-[#1a8754]/50'
                    : 'text-[#b07503] bg-[#E8B102]/[0.08] border border-dashed border-[#E8B102]/50'"
            >
                <i :class="autoFixResolved(message) ? 'fa-solid fa-check' : 'fa-solid fa-wand-magic-sparkles'" class="text-[10px]" />
                <span>{{ autoFixLabel(message) }}</span>
            </div>
            <div class="bg-white border border-[#E7E7E7] rounded-[12px] rounded-bl-[4px] px-4 py-3 shadow-[0_1px_2px_rgba(0,0,0,0.03)] flex flex-col gap-[10px]">
                <div
                    v-if="message.content"
                    class="ai-markdown font-inter text-[13px] leading-[20px] text-gray-900 overflow-x-auto"
                    v-html="renderedContent(message.content)"
                />
                <AiArtifactCard
                    v-for="doc in documentsCreated(message)"
                    :key="`${doc.type}-${doc.id}`"
                    :type="doc.type"
                    :id="doc.id"
                    :title="getArtifactTitle(doc)"
                />
            </div>
            <span class="block mt-1 font-poppins text-[10px] text-[#abb5c0]">{{ formattedTime(message.timestamp) }}</span>
        </div>
    </div>
</template>
